import {FC, useState} from 'react'
import InfoTooltip from './InfoTooltip'
import {OverlayTrigger, Tooltip} from 'react-bootstrap'
import SequenceFieldShell from 'src/app/pages/listing/components/SequenceFieldShell'
import {useApplyPlatformSettingShortcodes} from 'src/utils/usePlatformSettingDisplayText'

const CustomInput: FC<{
  inputProps: React.DetailedHTMLProps<React.InputHTMLAttributes<HTMLInputElement>, HTMLInputElement>
  label: string
  message: string
  inputWrapperClasses?: string
  containerClasses?: string
  labelClass?: string
  showCopyButton?: boolean
  disabledReason?: string | null
  useSequenceBandLayout?: boolean
  sourceChannelName?: string | null
}> = ({
  inputProps,
  label = '',
  message = '',
  inputWrapperClasses = '',
  containerClasses = 'col-12 col-sm-6 mt-4',
  labelClass = `form-label ${inputProps?.required ? 'required' : ''}`,
  showCopyButton = false,
  disabledReason = null,
  useSequenceBandLayout = false,
  sourceChannelName,
}) => {
  const formatSettingDisplayText = useApplyPlatformSettingShortcodes(sourceChannelName)
  const displayInputProps = {
    ...inputProps,
    ...(inputProps.placeholder != null
      ? {placeholder: formatSettingDisplayText(String(inputProps.placeholder))}
      : {}),
  }
  const [copyLabel, setCopyLabel] = useState('Click to copy')
  const valueToCopy =
    inputProps.value === null || inputProps.value === undefined ? '' : String(inputProps.value)
  const isChannelDisabled = Boolean(inputProps.disabled && disabledReason)

  const handleCopy = async () => {
    if (!valueToCopy) return

    try {
      await navigator.clipboard.writeText(valueToCopy)
      setCopyLabel('Copied')
      window.setTimeout(() => setCopyLabel('Click to copy'), 1500)
    } catch (error) {
      const textarea = document.createElement('textarea')
      textarea.value = valueToCopy
      textarea.style.position = 'fixed'
      textarea.style.opacity = '0'
      document.body.appendChild(textarea)
      textarea.focus()
      textarea.select()
      document.execCommand('copy')
      document.body.removeChild(textarea)
      setCopyLabel('Copied')
      window.setTimeout(() => setCopyLabel('Click to copy'), 1500)
    }
  }

  const renderInput = () => {
    const inputElement = <input {...displayInputProps} />

    if (showCopyButton) {
      return (
        <div className='d-flex align-items-stretch gap-2'>
          <input
            {...displayInputProps}
            className={`${inputProps.className ?? ''} bg-light text-muted`}
            style={{
              cursor: 'default',
              ...(inputProps.style ?? {}),
            }}
          />
          <OverlayTrigger
            trigger={['hover', 'focus']}
            delay={{show: 250, hide: 2000}}
            overlay={
              <Tooltip
                style={{
                  fontSize: '14px',
                  maxWidth: '500px',
                  maxHeight: '240px',
                  overflowY: 'auto',
                }}
              >
                <div style={{maxHeight: '220px', overflowY: 'auto'}}>{copyLabel}</div>
              </Tooltip>
            }
          >
            <button
              type='button'
              className='btn border text-muted d-inline-flex align-items-center justify-content-center'
              aria-label={copyLabel}
              onClick={handleCopy}
              style={{minWidth: '42px', backgroundColor: '#fff'}}
            >
              <i className='bi bi-clipboard' />
            </button>
          </OverlayTrigger>
        </div>
      )
    }

    if (isChannelDisabled) {
      return (
        <OverlayTrigger
          trigger={['hover', 'focus']}
          delay={{show: 250, hide: 200}}
          overlay={
            <Tooltip
              style={{
                fontSize: '12px',
                minWidth: '240px',
                maxWidth: '480px',
                whiteSpace: 'normal',
              }}
            >
              {disabledReason}
            </Tooltip>
          }
        >
          <span className='d-inline-block' tabIndex={0} style={{cursor: 'not-allowed'}}>
            {inputElement}
          </span>
        </OverlayTrigger>
      )
    }

    return inputElement
  }

  const labelNode = (
    <label className={labelClass} htmlFor={inputProps.id}>
      {formatSettingDisplayText(label)} &nbsp;&nbsp;
      <InfoTooltip message={message} sourceChannelName={sourceChannelName} />
    </label>
  )

  if (useSequenceBandLayout) {
    return (
      <div className={containerClasses}>
        <SequenceFieldShell
          label={labelNode}
          control={<div className={inputWrapperClasses}>{renderInput()}</div>}
        />
      </div>
    )
  }

  return (
    <div className={containerClasses} style={{paddingRight: '5.5rem'}}>
      {labelNode}
      <div className={inputWrapperClasses}>{renderInput()}</div>
    </div>
  )
}

export default CustomInput
